import type { Place } from '../astro/frame';
import { declination as fieldDeclination } from '../astro/wmm';
import { DEG, Mat3, Quat, Vec3, clamp, dot, enuToAltAz, mat3FromQuat, mat3Mul, quatAngle, quatFromMat3, quatSlerp, rotX, rotY, rotZ, wrap180 } from '../lib/math';

/**
 * Turns device sensor readings into a camera orientation in the local East-North-Up frame.
 *
 * iOS:     'deviceorientation' alpha is relative to an arbitrary start direction; the magnetic
 *          heading (webkitCompassHeading) is fused in as a slowly filtered azimuth offset, and
 *          nothing is shown as north until that offset has been learnt.
 * Android: AbsoluteOrientationSensor (screen frame) when available, else 'deviceorientationabsolute'.
 * Both are referenced to magnetic north; `declination` (WMM) converts to true north, and
 * `calibration` holds the user's star-alignment correction.
 */
/**
 * Camera→ENU matrix from W3C device orientation angles (degrees; alpha measured from magnetic
 * north, counter-clockwise), the screen rotation angle, and the east-positive declination plus
 * any user calibration. Exported for tests.
 */
export function cameraFromEuler(alpha: number, beta: number, gamma: number, screenAngle: number, declination: number, calibration = 0): Mat3 {
  const R = mat3Mul(mat3Mul(rotZ(alpha * DEG), rotX(beta * DEG)), rotY(gamma * DEG));
  const Rs = mat3Mul(R, rotZ(-screenAngle * DEG));
  return mat3Mul(rotZ(-(declination + calibration) * DEG), Rs);
}

/** Where a camera→ENU matrix looks: out of the back of the phone. */
export const forwardOf = (m: Mat3): Vec3 => [-m[2], -m[5], -m[8]];

/**
 * A correction of the reference (north learnt again, the compass settling on another reference, pointing taking over
 * from a dragged view) turns the view at up to 180° a second, easing in and out over 0.15 s: a half-turn takes about a
 * second, so people can see what happened. The phone's own motion keeps the quick smoothing in camToEnu.
 */
const GLIDE_RATE = Math.PI; // radians a second
const GLIDE_EASE = GLIDE_RATE / 0.15; // radians a second, per second
/** The glide is worked out in steps this long, so it takes the same path at any frame rate. */
const GLIDE_STEP = 1 / 240;
/**
 * A hand turns a phone at up to about 1000° a second, and its readings come every sixtieth of a second or so. A reading
 * whose aim has moved more than 25°, and faster than that, since the last one is the reference jumping, not the hand.
 */
const HAND_RATE = 1000; // degrees a second
const HAND_JUMP = 25; // degrees
/** A change of the reference itself (alignment, declination, an iPhone's learnt heading) larger than this glides. The
 * compass filter's own nudges stay under it (at most 1.8° a reading) and keep the usual smoothing. */
const REF_JUMP = 2; // degrees
const IDENTITY: Quat = [0, 0, 0, 1];
/** The turn b, then the turn a. */
const qMul = (a: Quat, b: Quat): Quat => [
  a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
  a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
  a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
  a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
];
/** The same turn backwards. */
const qInv = (q: Quat): Quat => [-q[0], -q[1], -q[2], q[3]];

/** iPhone or iPad (iPadOS reports itself as a Mac with a touch screen). */
const isIOSDevice = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

type DOEWithPermission = typeof DeviceOrientationEvent & { requestPermission?: () => Promise<string> };

export type SensorStatus = 'idle' | 'starting' | 'running' | 'denied' | 'unavailable';

interface IOSOrientationEvent extends DeviceOrientationEvent {
  webkitCompassHeading?: number;
  webkitCompassAccuracy?: number;
}

type AbsSensor = {
  quaternion: [number, number, number, number] | null;
  start(): void;
  stop(): void;
  addEventListener(t: string, f: (e: Event & { error?: { name: string } }) => void): void;
};

export class OrientationSource {
  status: SensorStatus = 'idle';
  declination = 0;
  calibration = 0; // degrees added to azimuth
  headingAccuracy = -1; // iOS: degrees, −1 unknown
  lastUpdate = 0;
  onChange?: () => void;

  /** The smoothed reading. */
  private q: Quat | null = null;
  private target: Quat | null = null;
  /** What is left of a correction still being turned through: the view shows lag∘q, and lag turns back to nothing. */
  private lag: Quat | null = null;
  /** How fast the view turns through `lag` now (radians a second). */
  private lagRate = 0;
  /** The reference in the latest reading: declination, alignment and an iPhone's learnt heading (degrees). */
  private ref = 0;
  private iosOffset: number | null = null;
  /** The last raw iPhone reading (iOS's own frame) and when it came, to catch that frame restarting. */
  private iosRaw: { R: Mat3; at: number } | null = null;
  private pendingJump = 0;
  private pendingSince = 0;
  private sensor: AbsSensor | null = null;
  private listeners: [string, EventListener][] = [];
  /** Motion readings have arrived since starting (on an iPhone, perhaps before north is known). */
  private reading = false;
  /** Counts stops, so a start still waiting for permission or a first reading knows it has been cancelled. */
  private gen = 0;
  /** The start in progress: asking again meanwhile gets the same answer. */
  private pending: Promise<SensorStatus> | null = null;
  private declKey = '';
  isIOS = false;

  /**
   * Start without a tap where the platform allows it: Android and others always, an iPhone only when
   * motion access has already been granted. 'needs-tap' when an iPhone has to ask first.
   */
  async startQuietly(): Promise<SensorStatus | 'needs-tap'> {
    if (this.status === 'running' || this.pending) return this.start();
    const gen = this.gen;
    const DOE = (window as unknown as { DeviceOrientationEvent?: DOEWithPermission }).DeviceOrientationEvent;
    if (DOE && isIOSDevice() && typeof DOE.requestPermission === 'function') {
      // Without a tap this answers only if access was granted before; otherwise it refuses, silently.
      try {
        if ((await DOE.requestPermission()) !== 'granted') return 'needs-tap';
      } catch {
        return 'needs-tap';
      }
      if (gen !== this.gen) return 'idle'; // stopped while asking
    }
    return this.start();
  }

  /** Must be called from a user gesture on iOS. Asked again while starting, it gives the same answer. */
  start(): Promise<SensorStatus> {
    if (this.status === 'running') return Promise.resolve('running');
    if (!this.pending) {
      const p: Promise<SensorStatus> = this.begin(this.gen).then((st) => {
        if (this.pending === p) this.pending = null;
        return st;
      });
      this.pending = p;
    }
    return this.pending;
  }

  /** One attempt to start. After every wait it checks that stop() has not been called meanwhile. */
  private async begin(gen: number): Promise<SensorStatus> {
    this.status = 'starting';
    const DOE = (window as unknown as { DeviceOrientationEvent?: DOEWithPermission }).DeviceOrientationEvent;
    if (!DOE) return this.settle(gen, 'unavailable');
    // iOS needs requestPermission() from a tap. Chrome 151+ also has the method (it reports the
    // current setting), so decide the platform separately rather than by its presence.
    this.isIOS = isIOSDevice();
    if (typeof DOE.requestPermission === 'function') {
      let r = 'failed';
      try {
        r = await DOE.requestPermission();
      } catch {
        /* a refusal on iOS; elsewhere, carry on */
      }
      // Stopped while asking: an answer that comes late installs nothing.
      if (gen !== this.gen) return 'idle';
      if (r === 'denied' || (this.isIOS && r !== 'granted')) return this.settle(gen, 'denied');
    }
    if (this.isIOS) {
      this.listen('deviceorientation', (e) => this.onIOS(e as IOSOrientationEvent));
      return this.settle(gen, await this.awaitFirst(gen));
    }
    // Android / others: Generic Sensor API first.
    const Ctor = (window as unknown as { AbsoluteOrientationSensor?: new (o: object) => AbsSensor }).AbsoluteOrientationSensor;
    if (Ctor) {
      try {
        const perms = await Promise.all(
          (['accelerometer', 'gyroscope', 'magnetometer'] as unknown as PermissionName[]).map((n) =>
            navigator.permissions?.query({ name: n }).then((s) => s.state).catch(() => 'granted'),
          ),
        );
        if (gen !== this.gen) return 'idle';
        if (perms.every((s) => s !== 'denied')) {
          const s = new Ctor({ frequency: 60, referenceFrame: 'screen' });
          this.sensor = s;
          s.addEventListener('reading', () => this.onQuat(s.quaternion));
          // Failing, now or later, it hands over to the orientation events, unless it has been replaced or stopped.
          s.addEventListener('error', () => {
            if (this.sensor === s) this.listenAbsoluteEvents();
          });
          s.start();
          const st = await this.awaitFirst(gen, 1500);
          if (st !== 'unavailable') return this.settle(gen, st);
        }
      } catch {
        /* fall through */
      }
    }
    this.listenAbsoluteEvents();
    return this.settle(gen, await this.awaitFirst(gen));
  }

  /** Stop listening. A start still waiting for permission or a first reading is cancelled and answers 'idle'. */
  stop() {
    this.gen++;
    this.pending = null;
    this.detach();
    this.status = 'idle';
    this.q = this.target = this.lag = null;
    this.lagRate = 0;
    this.iosOffset = null;
    this.iosRaw = null;
    this.pendingSince = 0;
    this.reading = false;
    this.headingAccuracy = -1;
  }

  /**
   * Current camera→ENU matrix as shown. With no reading to follow (an iPhone that has not yet learnt north) it holds
   * the view it was seeded with or last showed; null if there is none.
   */
  camToEnu(dt: number): Mat3 | null {
    if (this.target) {
      if (!this.q) this.q = this.target;
      else {
        // Adaptive smoothing: calm hand jitter, follow deliberate motion quickly. Each sixtieth of a second closes the
        // same share of the gap, as tuned at 60 frames a second, so a slower frame rate never jumps the whole way at once.
        const ang = quatAngle(this.q, this.target);
        const share = (8 + 90 * Math.min(ang, 0.5)) / 60;
        const k = 1 - Math.pow(1 - share, 60 * Math.min(dt, 0.05));
        this.q = quatSlerp(this.q, this.target, k);
      }
      // A correction, meanwhile, glides at its own pace (the hand is still followed at once, on top of it).
      if (this.lag) this.glide(dt);
    }
    const v = this.shown();
    return v && mat3FromQuat(v);
  }

  /** The view as shown: the smoothed reading, turned by what is left of any correction. */
  private shown(): Quat | null {
    return this.q && (this.lag ? qMul(this.lag, this.q) : this.q);
  }

  /**
   * Turn on through what is left of a correction: up to GLIDE_RATE, speeding up and slowing down at GLIDE_EASE so it
   * stops just there. Worked out in short fixed steps, so a frame of 1/30 s goes exactly as far as two of 1/60 s.
   */
  private glide(dt: number) {
    const lag = this.lag!;
    const left = quatAngle(IDENTITY, lag);
    let rest = left;
    let w = this.lagRate;
    for (let t = dt; t > 1e-9 && rest > 0; t -= GLIDE_STEP) {
      const h = Math.min(t, GLIDE_STEP);
      w = Math.min(w + GLIDE_EASE * h, GLIDE_RATE, Math.sqrt(2 * GLIDE_EASE * rest));
      rest = Math.max(0, rest - w * h);
    }
    if (rest < 1e-6) {
      this.lag = null;
      this.lagRate = 0;
    } else {
      this.lag = quatSlerp(IDENTITY, lag, rest / left);
      this.lagRate = w;
    }
  }

  /** Show `view` now and glide from it to the reading, or hold it until there is one. */
  private glideFrom(view: Quat) {
    this.lagRate = 0;
    if (this.target) {
      this.q = this.target;
      this.lag = qMul(view, qInv(this.target));
    } else {
      this.q = view;
      this.lag = null;
    }
  }

  /** The reference has jumped by `jump` (a turn of the ENU frame) with no turn of the phone: the view stays put, then glides. */
  private fold(jump: Quat) {
    if (!this.q) return; // nothing shown yet
    this.q = qMul(jump, this.q);
    this.lag = qMul(this.lag ?? IDENTITY, qInv(jump));
  }

  /** Whether the view now follows a reading tied to north (an iPhone learning north again holds the view still). */
  get northKnown(): boolean {
    return this.target !== null;
  }

  /** Where the back of the phone points now (ENU), from the latest reading without smoothing; null without one (or, on
   * an iPhone, while north is unknown). */
  latestForward(): Vec3 | null {
    return this.target ? forwardOf(mat3FromQuat(this.target)) : null;
  }

  /**
   * Align: correct the calibration so that where the phone points by its sensors is `seen` (ENU, as it appears in the
   * sky). Measured against the reading, not the view shown, which may still be gliding to a correction (aligned then,
   * the glide would be saved as a compass error). Gives the correction in degrees, or null with no reading.
   */
  alignTo(seen: Vec3): number | null {
    const reading = this.latestForward();
    if (!reading) return null;
    const delta = wrap180(enuToAltAz(seen).az - enuToAltAz(reading).az);
    this.calibration = wrap180(this.calibration + delta);
    return delta;
  }

  /** Start the view from this orientation, so taking over from a dragged view glides to where the phone points. */
  seed(camToEnu: Mat3) {
    this.glideFrom(quatFromMat3(camToEnu));
  }

  /**
   * After the page returns from the background. iOS may have restarted its motion frame, so north is learnt again;
   * until then the view holds where it was rather than show that frame's arbitrary north, then glides from there.
   */
  reseed() {
    if (!this.isIOS) return;
    this.iosOffset = null;
    this.iosRaw = null;
    this.pendingSince = 0;
    this.target = null;
    const v = this.shown();
    if (v) this.glideFrom(v);
  }

  /**
   * Magnetic declination for where the phone is, on the real date: the compass feels today's field there, whatever
   * place or moment the sky shows. Worked out again only when the place or the day changes.
   */
  updateDeclination(where: Place, now = Date.now()) {
    const key = `${where.lat.toFixed(2)},${where.lon.toFixed(2)},${Math.round(where.height)},${Math.floor(now / 864e5)}`;
    if (key === this.declKey) return;
    this.declKey = key;
    this.declination = fieldDeclination(where.lat, where.lon, where.height, new Date(now));
  }

  /** How a start ended, unless it was stopped meanwhile: then nothing changes and its caller hears 'idle'. */
  private settle(gen: number, st: SensorStatus): SensorStatus {
    if (gen !== this.gen) return 'idle';
    if (st !== 'running') this.detach();
    this.status = st;
    this.onChange?.();
    return st;
  }

  private listen(type: string, f: (e: Event) => void) {
    window.addEventListener(type, f as EventListener);
    this.listeners.push([type, f as EventListener]);
  }

  private detach() {
    for (const [t, f] of this.listeners) window.removeEventListener(t, f);
    this.listeners = [];
    this.sensor?.stop();
    this.sensor = null;
  }

  /** The absolute orientation events, in place of a Generic Sensor that failed or stayed silent. Installed once. */
  private listenAbsoluteEvents() {
    this.sensor?.stop();
    this.sensor = null;
    if (this.listeners.length) return;
    if ('ondeviceorientationabsolute' in window) {
      this.listen('deviceorientationabsolute', (e) => this.onEuler(e as DeviceOrientationEvent, true));
    } else {
      this.listen('deviceorientation', (e) => {
        const ev = e as DeviceOrientationEvent;
        if (ev.absolute) this.onEuler(ev, true);
      });
    }
  }

  /**
   * 'running' once readings arrive, 'unavailable' if none come in time, 'idle' if stopped meanwhile. An iPhone counts
   * as running before its compass settles: the view holds still until north is learnt, so starting never waits on it.
   */
  private awaitFirst(gen: number, timeout = 2500): Promise<SensorStatus> {
    return new Promise((resolve) => {
      const t0 = performance.now();
      const tick = () => {
        if (gen !== this.gen) resolve('idle');
        else if (this.reading) resolve('running');
        else if (performance.now() - t0 > timeout) resolve('unavailable');
        else setTimeout(tick, 50);
      };
      tick();
    });
  }

  private screenAngle(): number {
    const so = screen.orientation?.angle;
    if (typeof so === 'number') return so;
    const wo = (window as unknown as { orientation?: number }).orientation;
    return typeof wo === 'number' ? (wo + 360) % 360 : 0;
  }

  /** Device→world rotation (W3C Z-X'-Y'' Euler angles), before any screen rotation. */
  private rawMatrix(e: DeviceOrientationEvent): Mat3 | null {
    if (e.alpha == null || e.beta == null || e.gamma == null) return null;
    return mat3Mul(mat3Mul(rotZ(e.alpha * DEG), rotX(e.beta * DEG)), rotY(e.gamma * DEG));
  }

  private withScreen(R: Mat3): Mat3 {
    return mat3Mul(R, rotZ(-this.screenAngle() * DEG));
  }

  private deviceMatrix(e: DeviceOrientationEvent): Mat3 | null {
    const R = this.rawMatrix(e);
    return R && this.withScreen(R);
  }

  /**
   * A reading: `R` is how the phone is held in its sensor's frame, `offset` how far (degrees) that frame's north is from
   * magnetic north (an iPhone's learnt heading; 0 where the sensor knows north itself).
   */
  private finish(R: Mat3, offset = 0) {
    const now = performance.now();
    const ref = this.declination + this.calibration + offset;
    const t = quatFromMat3(mat3Mul(rotZ(-ref * DEG), R));
    const prev = this.target;
    this.target = t;
    if (!prev) {
      // North found, at the start or again after a return from the background: glide there from the view held meanwhile.
      const v = this.shown();
      if (v) this.glideFrom(v);
    } else {
      // The phone's own motion is continuous, reading by reading, and is followed at once. A jump of the aim that no
      // hand could make since the last reading, or a change of the reference itself, is a correction: it glides.
      const moved = Math.acos(clamp(dot(forwardOf(mat3FromQuat(prev)), forwardOf(mat3FromQuat(t))), -1, 1)) / DEG;
      const dRef = wrap180(ref - this.ref);
      if (moved > Math.max(HAND_JUMP, (HAND_RATE * (now - this.lastUpdate)) / 1000)) this.fold(qMul(t, qInv(prev)));
      else if (Math.abs(dRef) > REF_JUMP) this.fold(quatFromMat3(rotZ(-dRef * DEG)));
    }
    this.ref = ref;
    this.lastUpdate = now;
    this.reading = true;
  }

  private onEuler(e: DeviceOrientationEvent, _absolute: boolean) {
    const R = this.deviceMatrix(e);
    if (R) this.finish(R);
  }

  private onQuat(q: [number, number, number, number] | null) {
    if (!q) return;
    this.finish(mat3FromQuat([q[0], q[1], q[2], q[3]]));
  }

  private onIOS(e: IOSOrientationEvent) {
    const Rdev = this.rawMatrix(e);
    if (!Rdev) return;
    this.reading = true;
    // iOS's frame can also restart with the page on screen: the reading then turns about the vertical, its tilt the
    // same, by more than any hand could turn the phone since the last one. North is learnt again, as after a return
    // from the background (the view holds, then glides), rather than follow the frame's new, arbitrary north.
    const now = performance.now();
    const prev = this.iosRaw;
    if (prev && this.iosOffset !== null) {
      const turned = quatAngle(quatFromMat3(prev.R), quatFromMat3(Rdev)) / DEG;
      // Up in the phone's own axes is the matrix's last row: the same tilt leaves it where it was.
      const tilted = Math.acos(clamp(prev.R[6] * Rdev[6] + prev.R[7] * Rdev[7] + prev.R[8] * Rdev[8], -1, 1)) / DEG;
      if (tilted < 3 && turned > Math.max(HAND_JUMP, (HAND_RATE * (now - prev.at)) / 1000)) this.reseed();
    }
    this.iosRaw = { R: Rdev, at: now };
    const R = this.withScreen(Rdev);
    const heading = e.webkitCompassHeading;
    const acc = e.webkitCompassAccuracy ?? -1;
    this.headingAccuracy = acc;
    if (typeof heading === 'number' && heading >= 0 && acc >= 0 && acc <= 45) {
      // The compass measures one of two device axes: the rear camera or the top edge, whichever is nearer level.
      // iOS hands over at about 45° of tilt, and tilted back past that (looking up at the sky) the top edge points
      // behind you, so the reading turns by half a turn. Taking the reading as the camera's then swung the whole sky
      // round (1 Oct 2026, iPhone 17 Pro, looking about 45° up). So: the axis nearer level, and within about 10° of
      // the hand-over, whichever agrees with the heading already learnt.
      const camX = -Rdev[2], camY = -Rdev[5];
      const topX = Rdev[1], topY = Rdev[4];
      const camH = Math.hypot(camX, camY), topH = Math.hypot(topX, topY);
      const viaCam = camH > 0.15 ? wrap180(heading - Math.atan2(camX, camY) / DEG) : null;
      const viaTop = topH > 0.15 ? wrap180(heading - Math.atan2(topX, topY) / DEG) : null;
      const clear = Math.abs(camH - topH) > 0.25;
      let sample: number | null;
      if (clear) sample = camH > topH ? viaCam : viaTop;
      else if (this.iosOffset === null) sample = null; // wait for a pose with no doubt
      else {
        const off = this.iosOffset;
        const d = (s: number | null) => (s === null ? Infinity : Math.abs(wrap180(s - off)));
        sample = d(viaCam) <= d(viaTop) ? viaCam : viaTop;
      }
      // Only with the camera near level is there no doubt which axis the compass measures: only then may a large
      // change be the reference really moving. Elsewhere a reading far from the learnt heading is set aside.
      const level = clear && camH > topH;
      if (sample !== null) {
        if (this.iosOffset === null) this.iosOffset = sample;
        else {
          const diff = wrap180(sample - this.iosOffset);
          if (Math.abs(diff) > 60 && !level) {
            this.pendingSince = 0;
          } else if (Math.abs(diff) > 60) {
            // Accept a large change only if it persists (the reference really moved).
            if (!this.pendingSince || Math.abs(wrap180(sample - this.pendingJump)) > 20) {
              this.pendingJump = sample;
              this.pendingSince = now;
            } else if (now - this.pendingSince > 750) {
              this.iosOffset = sample;
              this.pendingSince = 0;
            }
          } else {
            this.pendingSince = 0;
            const gain = acc <= 10 ? 0.03 : acc <= 20 ? 0.015 : 0.006;
            this.iosOffset = wrap180(this.iosOffset + diff * gain);
          }
        }
      }
    }
    // Until a heading is learnt, north in iOS's frame is arbitrary (at start, and after a return from the background,
    // when the frame may have restarted). Publish nothing: the view holds where it was, then moves there once known.
    if (this.iosOffset !== null) this.finish(R, this.iosOffset);
  }
}
